<script setup lang="ts">
/**
 * Eine Discord-Nachricht im "cozy" Layout: 40px-Avatar links, Kopfzeile mit
 * Username + optional APP-Badge + Timestamp, darunter der Inhalt (Slot).
 * `compact` hängt eine Folge-Nachricht ohne Avatar/Kopf an die Gruppe an.
 */
withDefaults(defineProps<{
  author: string
  time?: string
  /** Der Bot selbst: Clarify-Logo als Avatar + APP-Badge */
  bot?: boolean
  /** Webhook-Relay: fremder Name/Avatar, aber Discord zeigt trotzdem das APP-Badge */
  webhook?: boolean
  color?: string
  avatarColor?: string
  compact?: boolean
}>(), {
  time: 'Today at 2:32 PM',
  bot: false,
  webhook: false,
  color: 'var(--dc-ink-header)',
  avatarColor: 'var(--dc-blurple)',
  compact: false
})
</script>

<template>
  <div class="dc-msg" :class="{ 'is-compact': compact }">
    <template v-if="!compact">
      <DiscordAvatar :name="author" :bot="bot" :color="avatarColor" :size="40" />
      <div class="dc-msg-body">
        <div class="dc-msg-head">
          <span class="dc-msg-author" :style="{ color }">{{ author }}</span>
          <span v-if="bot || webhook" class="dc-msg-badge">APP</span>
          <span class="dc-msg-time">{{ time }}</span>
        </div>
        <div class="dc-msg-content">
          <slot />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="dc-msg-gutter" />
      <div class="dc-msg-body">
        <div class="dc-msg-content">
          <slot />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.dc-msg {
  display: flex;
  gap: 16px;
  padding: 2px 16px;
  margin-top: 17px;
  font-family: var(--font-discord);
  border-radius: 2px;
}

.dc-msg.is-compact {
  margin-top: 2px;
}

.dc-msg:hover {
  background: var(--dc-bg-msg-hover);
}

.dc-msg-gutter {
  width: 40px;
  flex-shrink: 0;
}

.dc-msg-body {
  min-width: 0;
  flex: 1;
}

.dc-msg-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  line-height: 1.375rem;
}

.dc-msg-author {
  font-size: 1rem;
  font-weight: 500;
}

.dc-msg-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: var(--dc-blurple);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-radius: 3px;
  padding: 1px 5px;
  transform: translateY(-1px);
}

.dc-msg-time {
  font-size: 0.75rem;
  color: var(--dc-ink-muted);
}

.dc-msg-content {
  color: var(--dc-ink);
  font-size: 0.9925rem;
  line-height: 1.375rem;
  overflow-wrap: break-word;
}

.dc-msg-content :deep(.dc-mention) {
  background: var(--dc-mention);
  color: #c9cdfb;
  border-radius: 3px;
  padding: 0 2px;
  font-weight: 500;
}
</style>
